import { useState, useEffect, useCallback, useRef } from 'react';
import { config } from '../config';

const getSessionInfo = async (getValidAccessToken) => {
  try {
    const token = await getValidAccessToken();
    
    const response = await fetch(`${config.API_BASE}/session/info`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get session info: ${response.statusText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Session info error:', error);
    throw error;
  }
};

const checkSessionValidity = async (getValidAccessToken) => {
  try {
    const info = await getSessionInfo(getValidAccessToken);
    
    return {
      isValid: info.isValid,
      timeRemaining: info.timeRemaining,
      timeRemainingHours: info.timeRemainingHours,
      expiresAt: new Date(info.expiresISO),
      willExpireIn: (minutes) => info.timeRemaining < (minutes * 60)
    };
  } catch (error) {
    return {
      isValid: false,
      timeRemaining: 0,
      timeRemainingHours: 0,
      expiresAt: null,
      willExpireIn: () => true
    };
  }
};

const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return 'Expired';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Hook simplifié pour éviter les boucles
const useSessionMonitor = (getValidAccessToken, onExpiring, onExpired) => {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const intervalRef = useRef(null);
  const hasTriggeredExpiring = useRef(false);
  const hasTriggeredExpired = useRef(false);

  const checkSession = useCallback(async () => {
    if (!getValidAccessToken || isChecking) return;
    
    setIsChecking(true);
    try {
      const validity = await checkSessionValidity(getValidAccessToken);
      setSessionInfo(validity);
      
      // Éviter les alertes répétées
      if (!validity.isValid && !hasTriggeredExpired.current && onExpired) {
        hasTriggeredExpired.current = true;
        onExpired();
      } else if (validity.isValid && validity.willExpireIn(30) && !hasTriggeredExpiring.current && onExpiring) {
        hasTriggeredExpiring.current = true;
        onExpiring(validity.timeRemainingHours);
      }
      
      // Reset flags si la session redevient valide
      if (validity.isValid && validity.timeRemainingHours > 0.5) {
        hasTriggeredExpiring.current = false;
        hasTriggeredExpired.current = false;
      }
      
    } catch (error) {
      console.error('Session check error:', error);
    } finally {
      setIsChecking(false);
    }
  }, [getValidAccessToken, onExpiring, onExpired, isChecking]);
  
  // Démarrer la surveillance UNIQUEMENT quand getValidAccessToken est disponible
  useEffect(() => {
    if (!getValidAccessToken) {
      // Nettoyer l'interval existant
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Check immédiat une seule fois
    checkSession();
    
    // Interval de 10 minutes (plus conservateur)
    intervalRef.current = setInterval(checkSession, 10 * 60 * 1000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [!!getValidAccessToken]); // Seulement dépendant de l'existence de la fonction
  
  return {
    sessionInfo,
    isChecking,
    checkSession,
    formatTimeRemaining: sessionInfo ? formatTimeRemaining(sessionInfo.timeRemaining) : 'Unknown'
  };
};

export default {
  getSessionInfo,
  checkSessionValidity,
  formatTimeRemaining,
  useSessionMonitor
};